import ArrowBack from '@mui/icons-material/ArrowBack'
import ArrowForward from '@mui/icons-material/ArrowForward'
import Close from '@mui/icons-material/Close'
import Error from '@mui/icons-material/Error'
import { Box, Dialog } from '@mui/material'
import React, { useContext, useMemo, useCallback, useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import publishIcon from 'src/assets/publish-icon.png'
import { ProfileContext } from 'src/consumer/context/ProfileContext'
import { checkAndGetTargetValue } from 'src/consumer/helpers/utilFunctions'
import { postDataWithoutParams } from 'src/consumer/services/featureServices'
import { useScreenSize } from 'src/customHooks/customHooks'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import { MentorSetupContext } from 'src/mentor/context/MentorSetupContext'
import {
  getOnboardingType,
  isOnboardedAsDoctor,
  isOnboardedAsTherapist,
} from 'src/mentor/layout/utils'
import { getMPDataWithoutParams } from '../../services'
import LegalPoliciesListing from '../LegalTerms'
import LegalForm from '../LegalTerms/LegalForm'
import MentorHeader from '../MentorHeader'
import { BasicDetails } from './components/BasicDetailsSection'
import { MentoringDetails } from './components/mentoringDetailsSection/MentoringDetailsSection'
import { YourImagesSection } from './components/YourImagesSection'
import ProfileViewer from './components/ProfileViewer'
import {
  AgreementDialogActions,
  AgreementDialogContent,
  AgreementsDialogPaper,
  CancelAgreementButton,
  MentorPageContainer,
  MentorProfileActions,
  MentorProfilePageSkipButton,
  ProfileTabButton,
  ProfileTabButtonContainer,
  ProfileTabContainer,
  SaveMentorButton,
  SavingCircularProgress,
  WelcomeDialogActions,
  WelcomeDialogButtonPrimary,
  WelcomeDialogContent,
  WelcomeDialogGetStartedIcon,
  WelcomeDialogHeroButtons,
  WelcomeDialogHeroButtonsContainer,
  WelcomeDialogPaper,
  WelcomeDialogTitle,
} from './MentorProfilePageStyles'
import {
  getMentorProfileAllPagesWithTitlesChanges,
  getStringsForMentorWelcomePopup,
  mentorProfileSaveFunction,
  profileSaveFunction,
} from './utils'

function MentorProfile() {
  const location = useLocation()
  const navigate = useNavigate()
  const mentorProfileContext = useContext(MentorProfileContext)
  const mentorSetupContext = useContext(MentorSetupContext)
  
  // Extract pathname to avoid location object reference changes
  // Memoize pathname to prevent re-renders when location object reference changes
  const pathname = useMemo(() => location.pathname, [location.pathname])
  
  // Derive isEditing directly from URL to avoid state sync issues
  const isEditing = useMemo(() => {
    return (pathname || '').toLowerCase().includes('edit')
  }, [pathname])
  
  // Create stable props that only change when actual data changes
  // This prevents child components from re-rendering unnecessarily
  const stableProps = useMemo(() => {
    const mentorProfileData = mentorProfileContext?.mentorProfileData
    const allTags = mentorSetupContext?.allTags
    
    return {
      mentorProfileData,
      allTags,
      updateProfileData: mentorProfileContext?.updateProfileData,
      enteringFirstTime: mentorProfileContext?.enteringFirstTime,
      allLegalPolicies: mentorProfileContext?.allLegalPolicies,
      setDontShowKycModal: mentorProfileContext?.setDontShowKycModal,
      refreshLegalPolicies: mentorProfileContext?.refreshLegalPolicies,
    }
  }, [
    // Only depend on actual data identifiers that matter, not object references or functions
    mentorProfileContext?.mentorProfileData?.id,
    mentorProfileContext?.mentorProfileData?.userId,
    mentorSetupContext?.allTags?.length,
    mentorProfileContext?.enteringFirstTime,
    mentorProfileContext?.allLegalPolicies?.length,
  ])

  // Handlers - memoize to prevent content useMemo from recreating on every render
  const handleCancel = useCallback(() => {
    // Navigate back to profile page without "edit"
    if (pathname.toLowerCase().includes('edit')) {
      navigate('/mentor-home/profile', { replace: true })
    }
  }, [pathname, navigate])

  const handleEdit = useCallback(() => {
    // Update URL to include "edit" if not already there
    if (!pathname.toLowerCase().includes('edit')) {
      navigate('/mentor-home/profile/edit', { replace: true })
    }
  }, [pathname, navigate])

  // Memoize the entire content to prevent re-renders when context object reference changes
  // but actual data hasn't changed
  // Use stable identifiers instead of object references for dependencies
  const content = useMemo(() => {
    return !isEditing ? (
      <ProfileViewer
        key="profile-viewer"
        onEdit={handleEdit}
        profileData={stableProps.mentorProfileData}
        allTags={stableProps.allTags}
      />
    ) : (
      <ProfileDataComponent
        key="profile-editor"
        profileData={stableProps.mentorProfileData}
        allLegalPolicies={stableProps.allLegalPolicies}
        enteringFirstTime={stableProps.enteringFirstTime}
        refreshLegalPolicies={stableProps.refreshLegalPolicies}
        setDontShowKycModal={stableProps.setDontShowKycModal}
        updateProfileData={stableProps.updateProfileData}
        formContextData={{ domains: stableProps.allTags }}
        slugInitialUrl={'https://aumhum.com/mentor/'}
        saveObjectTransform={mentorProfileSaveFunction}
        onCancel={handleCancel}
      />
    )
  }, [
    isEditing,
    // Use stable identifiers instead of object references
    stableProps.mentorProfileData?.id,
    stableProps.mentorProfileData?.userId,
    stableProps.allTags?.length,
    stableProps.allLegalPolicies?.length,
    stableProps.enteringFirstTime,
    // handleEdit and handleCancel are memoized with useCallback, so they're stable
    handleEdit,
    handleCancel,
  ])
  
  return (
    <>
      <MentorHeader greetingsEnabled={true} />
      {content}
    </>
  )
}

export function ProfileDataComponent({
  profileData: mentorProfileData,
  updateProfileData,
  enteringFirstTime,
  allLegalPolicies = [],
  setDontShowKycModal,
  refreshLegalPolicies,
  formContextData = {},
  slugInitialUrl,
  saveObjectTransform = (formData) => {
    return formData
  },
  saveApiToUse = '/api/secure/teacher/save',
  tabLabelAndComponentChanger = {},
  onCancel,
}) {
  const { profileData } = useContext(ProfileContext)

  const mentorProfileAllPages = getMentorProfileAllPagesWithTitlesChanges({
    onboardingType: getOnboardingType(),
  })

  const pages = {
    ...mentorProfileAllPages,
    agreements: allLegalPolicies?.length > 0 ? mentorProfileAllPages?.agreements : null,
  }

  const [formState, setFormState] = useState({})
  const [currentPage, setCurrentPage] = useState(pages?.basic?.pageValue)
  const [changesMade, setChangesMade] = useState(false)
  const [savingData, setSavingData] = useState(false)
  const screenSize = useScreenSize()
  const [showWelcome, setShowWelcome] = useState(false)
  const [slugValid, setSlugValid] = useState(false)
  const pageValuesInOrder = Object.entries(pages)
    ?.filter(([page, pageValues]) => !!pageValues)
    .map(([page, pageValues]) => pageValues)
  const currentPageIndex = pageValuesInOrder
    ?.map((pageObj) => pageObj?.pageValue)
    ?.indexOf(currentPage)
  const [agreementModalProps, setAgreementModalProps] = useState({
    open: false,
    agreementId: '',
    onClose: null,
  })
  const MentoringDetailsPageComponent = useMemo(() => {
    return tabLabelAndComponentChanger?.[pages?.mentoring?.pageValue]?.component
      ? tabLabelAndComponentChanger?.[pages?.mentoring?.pageValue]?.component
      : MentoringDetails
  }, [tabLabelAndComponentChanger])

  async function handleSubmit(event) {
    setSavingData(true)
    const form = event.currentTarget
    if (form.checkValidity() === false) {
      event.preventDefault()
      event.stopPropagation()
    } else {
      event.preventDefault()
      var toSubmit = profileSaveFunction(formState)
      toSubmit = await saveObjectTransform(toSubmit)

      const response = await postDataWithoutParams(saveApiToUse, toSubmit)
      if (response.status === 200) {
        refreshLegalPolicies && refreshLegalPolicies()
        setChangesMade(false)
        console.log({ updateProfileData })
        updateProfileData()
        toast.success('Profile Saved Successfully')
        // If onCancel is provided, call it to return to viewer mode
        if (onCancel) {
          onCancel()
        } else {
          currentPageIndex < pageValuesInOrder?.length - 1 && window.scrollTo(0, 0)
          setCurrentPage((prev) => {
            const foundIndex = currentPageIndex
            return pageValuesInOrder?.[foundIndex + 1]?.pageValue || prev
          })
        }
        setDontShowKycModal(true)
      } else {
        toast.error('Something went wrong while saving your profile.')
      }
    }
    setSavingData(false)
  }
  function skipPage() {
    window.scrollTo(0, 0)
    setCurrentPage((prev) => {
      return pageValuesInOrder?.[currentPageIndex + 1]?.pageValue || prev
    })
  }

  function goBack() {
    window.scrollTo(0, 0)
    setCurrentPage((prev) => {
      const foundIndex = currentPageIndex
      return foundIndex >= 0 ? pageValuesInOrder?.[foundIndex - 1]?.pageValue : prev
    })
  }

  function handleChange(event) {
    setChangesMade(true)
    setFormState((prev) => ({ ...prev, [event.target.id]: checkAndGetTargetValue(event) }))
  }

  function handleNestedChange(event, outerId) {
    setChangesMade(true)
    setFormState((prev) => ({
      ...prev,
      [outerId]: {
        ...(prev?.[outerId] || {}),
        [event.target.id]: checkAndGetTargetValue(event),
      },
    }))
  }

  function formStateSetter(prev) {
    setFormState(prev)
    setChangesMade(true)
  }

  function handleClose() {
    setShowWelcome(false)
    setAgreementModalProps((prev) => ({
      open: false,
      onClose: handleClose,
    }))
  }

  function clickAgreement(agreementId) {
    setAgreementModalProps((prev) => ({
      ...prev,
      agreementId: agreementId,
      open: true,
      onClose: handleClose,
    }))
  }

  async function Setup() {
    const dataToPush = mentorProfileData
    var allTechniques = []
    if (!!dataToPush?.personalize?.techniquesSpecializedIn) {
      allTechniques = await Promise.all(
        dataToPush?.personalize?.techniquesSpecializedIn?.map(async (techniqueId) => {
          const resp = await getMPDataWithoutParams(`/api/secure/technique/${techniqueId}`)
          if (resp.status === 200) {
            return resp.data
          } else {
            return ''
          }
        }),
      )
    }

    var languagesToSet = dataToPush?.languages

    if (languagesToSet?.length === 0 || !languagesToSet) {
      if (profileData?.languages?.length > 0) {
        languagesToSet = profileData?.languages
      } else {
        ///set browser language here
        const navigatorLang = navigator.language || navigator.userLanguage
        if (navigatorLang?.split('-')?.[0]) {
          languagesToSet = [navigatorLang?.split('-')?.[0]]
        } else {
          languagesToSet = ['en']
        }
      }
      languagesToSet = ['hi', ...languagesToSet?.filter((lang) => lang !== 'hi')]
    }

    // const qualificationsToSet = (isOnboardedAsDoctor() && isJustOnboardedTeacher()) ? [dataToPush?.qualifications?.filter(quali=>!"")] : dataToPush?.qualifications

    setFormState({
      ...dataToPush,
      categories: isOnboardedAsDoctor()
        ? [...(dataToPush?.categories || [])?.filter((category) => category !== 'doctor'), 'doctor']
        : dataToPush?.categories,

      personalize: { ...dataToPush?.personalize, techniquesSpecializedIn: allTechniques },
      languages: languagesToSet,
    })
  }

  useEffect(() => {
    Setup()
  }, [mentorProfileData])

  useEffect(() => {
    console.log(formState)
  }, [formState])
  useEffect(() => {
    // console.log(changesMade)
  }, [changesMade])

  function renderPage() {
    switch (currentPage) {
      case pages?.basic?.pageValue:
        return (
          <BasicDetails
            formState={formState}
            setFormState={formStateSetter}
            handleChange={handleChange}
            handleNestedChange={handleNestedChange}
            setButtonValidation={setSlugValid}
            profileData={mentorProfileData}
            slugInitialUrl={slugInitialUrl}
            dropDownContextData={formContextData}
          />
        )
      case pages?.mentoring?.pageValue:
        return (
          <MentoringDetailsPageComponent
            formState={formState}
            setFormState={formStateSetter}
            setPage={setCurrentPage}
            handleChange={handleChange}
            handleNestedChange={handleNestedChange}
            dropDownContextData={formContextData}
          />
        )
      case pages?.images?.pageValue:
        return (
          <YourImagesSection
            formState={formState}
            setFormState={formStateSetter}
            setPage={setCurrentPage}
            handleChange={handleChange}
            handleNestedChange={handleNestedChange}
          />
        )
      case pages?.agreements?.pageValue:
        return (
          <LegalPoliciesListing
            header={false}
            tabs={false}
            // mentorProfileData={formSat}
            customOnClick={clickAgreement}
            // showOnlyNotAccepted
          />
        )
      default:
        return <div>Coming Soon....</div>
    }
  }

  useEffect(() => {
    setShowWelcome(enteringFirstTime)
  }, [])

  return (
    <>
      <MentorPageContainer onSubmit={handleSubmit}>
        <div style={{ flexGrow: 1 }}>
          <ProfileTabs
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            allLegalPolicies={allLegalPolicies}
            tabLabelAndComponentChanger={tabLabelAndComponentChanger}
          />
          {renderPage()}
        </div>
        {currentPage !== pages?.agreements?.pageValue && (
          <MentorProfileActions>
            {onCancel ? (
              <MentorProfilePageSkipButton onClick={onCancel}>
                <ArrowBack /> {screenSize.width > 576 && <span>Cancel</span>}
              </MentorProfilePageSkipButton>
            ) : (
              <MentorProfilePageSkipButton onClick={goBack} disabled={currentPageIndex === 0}>
                <ArrowBack /> {screenSize.width > 576 && <span>Back</span>}
              </MentorProfilePageSkipButton>
            )}
            <Box sx={{ m: 1, position: 'relative' }}>
              <SaveMentorButton
                type="submit"
                disabled={
                  !!slugInitialUrl
                    ? !changesMade || savingData || !slugValid
                    : !changesMade || savingData
                }
              >
                {currentPageIndex < pageValuesInOrder?.length - 1 ? 'Save and proceed' : 'Save'}
                {savingData && (
                  <SavingCircularProgress
                    size={24}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      marginTop: '-12px',
                      marginLeft: '-12px',
                    }}
                  />
                )}
              </SaveMentorButton>
            </Box>
            {!onCancel && (
              <MentorProfilePageSkipButton
                backgroundColorOverride={!changesMade && slugValid ? '#03D63E' : undefined}
                onClick={skipPage}
              >
                {screenSize.width > 576 && <span>Skip</span>} <ArrowForward />
              </MentorProfilePageSkipButton>
            )}
          </MentorProfileActions>
        )}
      </MentorPageContainer>
      <WelcomeMentorDialog open={showWelcome} onClose={handleClose} />
      {!!allLegalPolicies && allLegalPolicies?.length !== 0 && (
        <AgreementsDialog {...agreementModalProps} />
      )}
    </>
  )
}

function ProfileTabs({
  currentPage,
  setCurrentPage,
  allLegalPolicies,
  tabLabelAndComponentChanger,
}) {
  const mentorProfileAllPages = getMentorProfileAllPagesWithTitlesChanges({
    onboardingType: getOnboardingType(),
  })

  const pages = {
    ...mentorProfileAllPages,
    agreements: allLegalPolicies?.length > 0 ? mentorProfileAllPages?.agreements : null,
  }
  const showAgreementsAlert = allLegalPolicies?.filter((agreement) => !agreement?.signed)?.length

  function changePage(pageValue) {
    setCurrentPage(pageValue)
  }

  return (
    <ProfileTabButtonContainer>
      <ProfileTabContainer>
        {Object.entries(pages)
          ?.filter(([label, values]) => !!values)
          .map(([label, values]) => (
            <ProfileTabButtonComponent
              key={label}
              pageValue={values?.pageValue}
              label={
                tabLabelAndComponentChanger?.[values?.pageValue]?.label
                  ? tabLabelAndComponentChanger?.[values?.pageValue]?.label
                  : values?.label
              }
              currentPage={currentPage}
              onClick={changePage}
              alert={
                showAgreementsAlert ? values?.pageValue === pages?.agreements?.pageValue : false
              }
            />
          ))}
      </ProfileTabContainer>
    </ProfileTabButtonContainer>
  )
}

function WelcomeMentorDialog(props) {
  const [page, setPage] = useState(1)

  function handleClose(event, reason) {
    if (reason && reason === 'backdropClick') {
      return
    }
    props.onClose(event, reason)
  }

  function nextPg() {
    setPage((prev) => prev + 1)
  }

  const onboardingType = getOnboardingType()

  const pageStrings = getStringsForMentorWelcomePopup({
    onboardingType,
  })

  function renderPage() {
    switch (page) {
      case 1:
        return (
          <>
            <WelcomeDialogTitle>{pageStrings?.page1?.title}</WelcomeDialogTitle>
            <WelcomeDialogContent>
              {pageStrings?.page1?.subtitle}
              <br />
              <br />
              {pageStrings?.page1?.content}
            </WelcomeDialogContent>
            <WelcomeDialogActions>
              <WelcomeDialogButtonPrimary
                onClick={isOnboardedAsDoctor() && isOnboardedAsTherapist() ? nextPg : handleClose}
              >
                {pageStrings?.page1?.buttonText}
              </WelcomeDialogButtonPrimary>
            </WelcomeDialogActions>
          </>
        )
      case 2:
        return (
          <>
            <WelcomeDialogTitle>{pageStrings?.page2?.title}</WelcomeDialogTitle>
            <WelcomeDialogContent>
              {pageStrings?.page2?.subtitle}
              {isOnboardedAsDoctor() && isOnboardedAsTherapist() && (
                <WelcomeDialogHeroButtonsContainer>
                  <WelcomeDialogHeroButtons>
                    <WelcomeDialogGetStartedIcon src={publishIcon} />
                    Publish Your courses
                    {/* <WelcomeDialogHeroButtonsCheck /> */}
                  </WelcomeDialogHeroButtons>

                  <WelcomeDialogHeroButtons>
                    <WelcomeDialogGetStartedIcon src={publishIcon} />
                    Conduct 1 - 1 Sessions
                    {/* <WelcomeDialogHeroButtonsCheck /> */}
                  </WelcomeDialogHeroButtons>
                </WelcomeDialogHeroButtonsContainer>
              )}
            </WelcomeDialogContent>
            <WelcomeDialogActions>
              <WelcomeDialogButtonPrimary onClick={handleClose}>
                {pageStrings?.page2?.buttonText}
              </WelcomeDialogButtonPrimary>
              {/* <WelcomeDialogButtonSecondary onClick={handleClose}>
                I&apos;ll do it later
              </WelcomeDialogButtonSecondary> */}
            </WelcomeDialogActions>
          </>
        )
      default:
        return <></>
    }
  }

  return (
    <Dialog PaperComponent={WelcomeDialogPaper} onClose={handleClose} disable {...props}>
      {renderPage()}
    </Dialog>
  )
}

function AgreementsDialog(props) {
  function handleClose(event, reason) {
    if (reason && reason === 'backdropClick') {
      return
    }
    props.onClose(event, reason)
  }

  return (
    <Dialog PaperComponent={AgreementsDialogPaper} onClose={handleClose} disable {...props}>
      <AgreementDialogActions>
        <CancelAgreementButton onClick={handleClose}>
          <Close />
        </CancelAgreementButton>
      </AgreementDialogActions>
      <AgreementDialogContent>
        <LegalForm
          showLayoutHeader={false}
          showPageHeader={false}
          agreementId={props.agreementId}
          afterSubmitFunction={handleClose}
        />
      </AgreementDialogContent>
    </Dialog>
  )
}

function ProfileTabButtonComponent({ pageValue, currentPage, onClick, label, alert = false }) {
  function handleClick() {
    onClick(pageValue)
  }

  return (
    <ProfileTabButton selected={pageValue === currentPage} onClick={handleClick}>
      {label}
      {alert && <Error sx={{ color: pageValue !== currentPage && '#dd4646' }} />}
    </ProfileTabButton>
  )
}

export default MentorProfile
